import setCookie from 'set-cookie-parser';
import { getLogger } from '@rakutenfrance/logger';
import { serialize } from 'cookie';
import { Headers } from 'node-fetch';
const logger = getLogger('redux-apollo-helper-cookiesUtils');
import { playSetCookies } from './genericService';
function addToSetCookie(mapHeaders, key, value) {
    var _a;
    if (!mapHeaders.has(key)) {
        mapHeaders.set(key, new Set());
    }
    if (Array.isArray(value)) {
        value.forEach((v) => { var _a; return (_a = mapHeaders.get(key)) === null || _a === void 0 ? void 0 : _a.add(v); });
    }
    else {
        // Detect problematic comma-separated cookies
        if (value.includes(', ') && value.match(/;\s*[^,]+=[^,]+(;\s*[^,=]+=[^,]*)*,/)) {
            logger.warn('⚠️  Malformed set-cookie header detected (comma-separated cookies):', value);
        }
        (_a = mapHeaders.get(key)) === null || _a === void 0 ? void 0 : _a.add(value);
    }
}
/**
 * Merges headers into a Map containing only 'set-cookie' values.
 * Other headers like CSP are intentionally excluded per original design.
 * @param headers - Headers object or Map containing header key-value pairs
 * @returns Map with 'set-cookie' header values stored in a Set
 */
export function mergeHeaders(headers) {
    const key = 'set-cookie';
    const mapHeaders = new Map();
    if (headers) {
        // Handle Headers-like objects (with forEach)
        if (headers instanceof Headers ||
            (typeof headers.get === 'function' && typeof headers.forEach === 'function' && !(headers instanceof Map))) {
            headers.forEach((value, headerKey) => {
                if (headerKey.toLowerCase() === key) {
                    addToSetCookie(mapHeaders, key, value);
                }
            });
        }
        // Handle Map
        else if (headers instanceof Map) {
            const value = headers.get(key);
            if (value)
                addToSetCookie(mapHeaders, key, value);
        }
        // Handle plain objects (Axios headers, etc.)
        else if (typeof headers === 'object') {
            const value = headers[key];
            if (value)
                addToSetCookie(mapHeaders, key, value);
        }
        else {
            // Fallback or throw an error
            throw new Error('Headers class is not available in this Node.js version. Please use Node.js 18+ with --experimental-fetch or upgrade to Node.js 20+.');
        }
    }
    return mapHeaders;
}
export function parseCookies(attribute) {
    const cookieStrings = setCookie.splitCookiesString(attribute);
    return setCookie.parse(cookieStrings);
}
export function mergeCookies(mapCookie, cookies) {
    const restrictionSet = new Set(playSetCookies());
    if (Array.isArray(cookies)) {
        cookies
            .filter((cookie) => restrictionSet.has(cookie.name))
            .forEach((cookie) => {
            mapCookie.set(cookie.name, cookie);
        });
    }
}
//==========================================================
//==========================================================
/**
 * Parses and merges cookie headers into a Map of cookie objects
 * @param mapSetCookieHeaders - Map containing set-cookie header values
 * @returns Map of cookie objects keyed by cookie name
 */
export function parseAndMergeCookieHeaders(mapSetCookieHeaders) {
    const cookieMap = new Map();
    const values = Array.from(mapSetCookieHeaders.values());
    values.forEach((value) => {
        value.forEach((cookieString) => {
            const cookies = parseCookies(cookieString);
            mergeCookies(cookieMap, cookies);
        });
    });
    return cookieMap;
}
/**
 * Serializes cookie objects back to cookie strings with proper expiration handling
 * @param cookieMap - Map of cookie objects
 * @returns Array of serialized cookie strings ready for Set-Cookie headers
 */
export function serializeCookieMap(cookieMap) {
    const setCookieStrings = [];
    cookieMap.forEach((cookie) => {
        if (cookie.maxAge) {
            // Ensure maxAge is properly set and calculate expires
            cookie.expires = new Date(Date.now() + cookie.maxAge * 1000);
        }
        setCookieStrings.push(serialize(cookie.name, String(cookie.value), cookie));
    });
    return setCookieStrings;
}
/**
 * Combined function that parses cookie headers and returns serialized cookie strings
 * @param mapSetCookieHeaders - Map containing set-cookie header values
 * @returns Array of serialized cookie strings
 */
export function processCookieHeaders(mapSetCookieHeaders) {
    const cookieMap = parseAndMergeCookieHeaders(mapSetCookieHeaders);
    return serializeCookieMap(cookieMap);
}
/**
 * Normalizes set-cookie header value to an array of strings
 * @param headerValue - The set-cookie header value (string, array, or undefined)
 * @returns Array of cookie strings
 */
export function normalizeSetCookieHeader(headerValue) {
    if (typeof headerValue === 'string') {
        return [headerValue];
    }
    if (Array.isArray(headerValue)) {
        return headerValue;
    }
    return [];
}
/**
 * Merges existing set-cookie headers with new ones
 * @param existingCookies - Current set-cookie header value
 * @param newCookies - Array of new cookie strings to add
 * @returns Combined array of cookie strings
 */
export function mergeSetCookieHeaders(existingCookies, newCookies) {
    const existing = normalizeSetCookieHeader(existingCookies);
    const mergedCookies = [...existing, ...newCookies];
    // logger.debug('mergedCookies', mergedCookies);
    return mergedCookies;
}
